import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import API from '../api/client';
import { ArrowLeft, Plus, Calendar, FileText, Activity, Apple, Heart, Printer, Edit, Download, Bell, Sparkles, X, Loader, CheckCircle, AlertCircle, Clock } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import ReactMarkdown from 'react-markdown';

const BADGE_COLORS = { diabete: 'bg-orange-100 text-orange-700', nutrition: 'bg-emerald-100 text-emerald-700', hta: 'bg-red-100 text-red-700', presc: 'bg-blue-100 text-blue-700' };
const PRIORITE_CONFIG = {
  urgent: { color: 'text-red-600 bg-red-50 border-red-200', icon: AlertCircle },
  normal: { color: 'text-amber-600 bg-amber-50 border-amber-200', icon: Clock },
  faible: { color: 'text-stone-500 bg-stone-50 border-stone-200', icon: Bell },
};
const EMPTY_REMINDER = { titre: '', description: '', date_rappel: '', heure_rappel: '', priorite: 'normal' };
const MODELS = [
  { value: 'gpt', label: 'GPT-5.2 (OpenAI)' },
  { value: 'gemini', label: 'Gemini Flash (Google)' },
];

export default function PatientProfilePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [patient, setPatient] = useState(null);
  const [consultations, setConsultations] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [reminders, setReminders] = useState([]);
  // AI summary
  const [aiSummary, setAiSummary] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiModel, setAiModel] = useState('gpt');
  // Reminder modal
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [reminderForm, setReminderForm] = useState(EMPTY_REMINDER);
  const [savingReminder, setSavingReminder] = useState(false);

  const loadReminders = () => API.get('/reminders', { params: { patient_id: id } }).then(r => setReminders(r.data));

  useEffect(() => {
    Promise.all([
      API.get(`/patients/${id}`),
      API.get('/consultations', { params: { patient_id: id } }),
      API.get('/appointments', { params: { patient_id: id } }),
      API.get('/reminders', { params: { patient_id: id } }),
    ]).then(([p, c, a, rem]) => {
      setPatient(p.data);
      setConsultations(c.data);
      setAppointments(a.data);
      setReminders(rem.data);
    });
  }, [id]);

  const handleExportPdf = () => {
    window.open(`${process.env.REACT_APP_BACKEND_URL}/api/patients/${id}/pdf`, '_blank');
  };

  const handleAiSummary = async () => {
    setAiLoading(true);
    setAiSummary('');
    try {
      const r = await API.post(`/ai/patient-summary/${id}`, { model: aiModel });
      setAiSummary(r.data.summary);
    } catch (e) {
      setAiSummary('Erreur lors de la génération du résumé IA.');
    } finally {
      setAiLoading(false);
    }
  };

  const handleSaveReminder = async () => {
    if (!reminderForm.titre || !reminderForm.date_rappel) return;
    setSavingReminder(true);
    try {
      await API.post('/reminders', { ...reminderForm, patient_id: parseInt(id) });
      setShowReminderModal(false);
      setReminderForm(EMPTY_REMINDER);
      loadReminders();
    } finally {
      setSavingReminder(false);
    }
  };

  const handleDoneReminder = async (rid) => {
    await API.put(`/reminders/${rid}`, { statut: 'terminé' });
    loadReminders();
  };

  const handleDeleteReminder = async (rid) => {
    await API.delete(`/reminders/${rid}`);
    loadReminders();
  };

  if (!patient) return (
    <div className="flex items-center justify-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
    </div>
  );

  const age = patient.date_naissance
    ? new Date().getFullYear() - new Date(patient.date_naissance).getFullYear()
    : null;

  const InfoItem = ({ label, value }) => (
    <div>
      <p className="text-xs text-stone-500 font-medium uppercase tracking-wide">{label}</p>
      <p className="text-sm text-stone-800 font-medium mt-0.5">{value || '—'}</p>
    </div>
  );

  const VisitCard = ({ c }) => {
    const hasDiabetes = c.diabete_type || c.diabete_hba1c || c.diabete_glycemie_a_jeun;
    const hasNutrition = c.nutrition_type_regime || c.nutrition_tour_de_taille;
    const hasHTA = c.hta_stade || c.hta_traitement;
    const hasPresc = c.prescriptions?.length > 0 && c.prescriptions[0]?.medicaments?.length > 0;

    return (
      <div className="bg-white border border-stone-200 rounded-xl p-5 hover:shadow-md transition-all" data-testid="visit-card">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div className="flex-1">
            <div className="flex items-center gap-2 flex-wrap">
              <p className="font-semibold text-stone-800">{c.date}</p>
              {hasDiabetes && <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${BADGE_COLORS.diabete}`}><Activity size={11} className="inline mr-1" />Diabète</span>}
              {hasNutrition && <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${BADGE_COLORS.nutrition}`}><Apple size={11} className="inline mr-1" />Nutrition</span>}
              {hasHTA && <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${BADGE_COLORS.hta}`}><Heart size={11} className="inline mr-1" />HTA</span>}
              {hasPresc && <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${BADGE_COLORS.presc}`}><FileText size={11} className="inline mr-1" />Ordonnance</span>}
            </div>
            <p className="text-stone-600 text-sm mt-1">{c.motif_consultation || 'Consultation générale'}</p>
            {c.diagnostic && <p className="text-emerald-700 text-sm mt-1 font-medium">Diag: {c.diagnostic}</p>}
            <div className="flex flex-wrap gap-3 mt-2">
              {c.tension_systolique && <span className="text-xs text-stone-500">TA: <b>{c.tension_systolique}/{c.tension_diastolique}</b> mmHg</span>}
              {c.pouls && <span className="text-xs text-stone-500">Pouls: <b>{c.pouls}</b> bpm</span>}
              {c.imc && <span className="text-xs text-stone-500">IMC: <b>{c.imc}</b></span>}
              {c.diabete_hba1c && <span className="text-xs text-orange-600">HbA1c: <b>{c.diabete_hba1c}%</b></span>}
              {c.diabete_glycemie_a_jeun && <span className="text-xs text-orange-600">Gly. jeun: <b>{c.diabete_glycemie_a_jeun} mg/dL</b></span>}
            </div>
            {hasPresc && (
              <div className="mt-2 p-2.5 bg-blue-50 border border-blue-100 rounded-lg">
                <p className="text-xs font-semibold text-blue-700 mb-1">Ordonnance:</p>
                {c.prescriptions[0].medicaments.slice(0, 3).map((m, i) => (
                  <p key={i} className="text-xs text-blue-600">• {m.nom}{m.dosage ? ` — ${m.dosage}` : ''}</p>
                ))}
                {c.prescriptions[0].medicaments.length > 3 && (
                  <p className="text-xs text-blue-400">+{c.prescriptions[0].medicaments.length - 3} autres</p>
                )}
              </div>
            )}
          </div>
          <div className="flex flex-col gap-1.5">
            <button onClick={() => navigate(`/consultations/${c.id}`)} data-testid="edit-visit-btn"
              className="flex items-center gap-1 px-3 py-1.5 border border-stone-300 bg-white text-stone-700 rounded-md text-xs font-medium hover:bg-stone-50 transition-colors">
              <Edit size={13} /> Modifier
            </button>
            <button onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/consultations/${c.id}/pdf`, '_blank')}
              data-testid="print-visit-btn"
              className="flex items-center gap-1 px-3 py-1.5 bg-emerald-600 text-white rounded-md text-xs font-medium hover:bg-emerald-700 transition-colors">
              <Printer size={13} /> PDF Visite
            </button>
          </div>
        </div>
      </div>
    );
  };

  const activeReminders = reminders.filter(r => r.statut === 'actif');

  return (
    <div className="space-y-5 animate-fade-in-up" data-testid="patient-profile-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <button onClick={() => navigate('/patients')} className="flex items-center gap-2 text-stone-500 hover:text-stone-700 text-sm font-medium transition-colors">
          <ArrowLeft size={16} /> Retour aux patients
        </button>
        <button onClick={handleExportPdf} data-testid="export-patient-pdf"
          className="flex items-center gap-1.5 px-4 py-2 border border-stone-300 bg-white text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-50 transition-colors">
          <Download size={16} /> Exporter Dossier PDF
        </button>
      </div>

      {/* Header patient */}
      <div className="bg-white border border-stone-200 rounded-xl p-5">
        <div className="flex items-start gap-4 flex-wrap">
          <div className="w-14 h-14 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
            <span className="text-emerald-700 font-bold text-xl">{patient.prenom?.[0]}{patient.nom?.[0]}</span>
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-3 flex-wrap">
              <h1 className="font-heading text-2xl font-bold text-stone-900">{patient.prenom} {patient.nom}</h1>
              {patient.groupe_sanguin && <span className="bg-red-100 text-red-700 text-xs font-semibold px-2.5 py-1 rounded-full">{patient.groupe_sanguin}</span>}
              <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${patient.sexe === 'M' ? 'bg-blue-100 text-blue-700' : 'bg-pink-100 text-pink-700'}`}>
                {patient.sexe === 'M' ? 'Homme' : 'Femme'}
              </span>
              {activeReminders.length > 0 && (
                <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2.5 py-1 rounded-full flex items-center gap-1">
                  <Bell size={11} /> {activeReminders.length} rappel{activeReminders.length > 1 ? 's' : ''}
                </span>
              )}
            </div>
            <div className="flex flex-wrap gap-5 mt-3">
              {age && <InfoItem label="Âge" value={`${age} ans`} />}
              <InfoItem label="CIN" value={patient.CIN} />
              <InfoItem label="CNSS/AMO" value={patient.CNSS_AMO} />
              <InfoItem label="Téléphone" value={patient.telephone} />
              <InfoItem label="Ville" value={patient.ville} />
              <InfoItem label="Couverture" value={patient.couverture_medicale} />
            </div>
          </div>
          <div className="flex flex-col gap-2">
            <button onClick={() => navigate(`/consultations/new?patient_id=${id}`)}
              data-testid="new-consultation-from-profile"
              className="flex items-center gap-1.5 px-3 py-2 bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg text-sm font-medium transition-colors">
              <Plus size={15} /> Nouvelle Visite
            </button>
            <button onClick={() => { setReminderForm(EMPTY_REMINDER); setShowReminderModal(true); }}
              data-testid="add-reminder-btn"
              className="flex items-center gap-1.5 px-3 py-2 border border-amber-300 bg-amber-50 hover:bg-amber-100 text-amber-700 rounded-lg text-sm font-medium transition-colors">
              <Bell size={15} /> Rappel
            </button>
          </div>
        </div>
        {patient.allergies && (
          <div className="mt-4 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2">
            <p className="text-xs font-semibold text-amber-700 uppercase">Allergies</p>
            <p className="text-sm text-amber-800 mt-0.5">{patient.allergies}</p>
          </div>
        )}
      </div>

      {/* Tabs */}
      <Tabs defaultValue="visites">
        <TabsList className="bg-stone-100 border border-stone-200 flex-wrap h-auto">
          <TabsTrigger value="visites">Visites ({consultations.length})</TabsTrigger>
          <TabsTrigger value="rappels">
            <Bell size={13} className="mr-1" />
            Rappels {activeReminders.length > 0 && `(${activeReminders.length})`}
          </TabsTrigger>
          <TabsTrigger value="ia">
            <Sparkles size={13} className="mr-1" />
            Résumé IA
          </TabsTrigger>
          <TabsTrigger value="info">Informations</TabsTrigger>
          <TabsTrigger value="rdv">Rendez-vous ({appointments.length})</TabsTrigger>
        </TabsList>

        {/* Visites */}
        <TabsContent value="visites">
          <div className="space-y-3">
            <div className="flex justify-end">
              <button onClick={() => navigate(`/consultations/new?patient_id=${id}`)}
                className="flex items-center gap-1.5 px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-medium hover:bg-emerald-700">
                <Plus size={15} /> Nouvelle visite
              </button>
            </div>
            {consultations.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-xl p-10 text-center">
                <FileText size={40} className="text-stone-200 mx-auto mb-3" />
                <p className="text-stone-400 text-sm">Aucune visite enregistrée</p>
              </div>
            ) : consultations.map(c => <VisitCard key={c.id} c={c} />)}
          </div>
        </TabsContent>

        {/* Rappels */}
        <TabsContent value="rappels">
          <div className="space-y-3">
            <div className="flex justify-end">
              <button
                data-testid="add-reminder-tab-btn"
                onClick={() => { setReminderForm(EMPTY_REMINDER); setShowReminderModal(true); }}
                className="flex items-center gap-1.5 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white rounded-lg text-sm font-medium transition-colors">
                <Plus size={15} /> Nouveau rappel
              </button>
            </div>
            {reminders.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-xl p-10 text-center">
                <Bell size={40} className="text-stone-200 mx-auto mb-3" />
                <p className="text-stone-400 text-sm">Aucun rappel pour ce patient</p>
              </div>
            ) : reminders.map(r => {
              const cfg = PRIORITE_CONFIG[r.priorite] || PRIORITE_CONFIG.normal;
              const Icon = cfg.icon;
              const today = new Date().toISOString().split('T')[0];
              const isOverdue = r.statut === 'actif' && r.date_rappel < today;
              return (
                <div key={r.id} data-testid="reminder-card" className={`bg-white border rounded-xl p-4 flex items-start gap-3 ${isOverdue ? 'border-red-200 bg-red-50/30' : 'border-stone-200'} ${r.statut === 'terminé' ? 'opacity-50' : ''}`}>
                  <Icon size={18} className={isOverdue ? 'text-red-500' : r.priorite === 'urgent' ? 'text-red-500' : 'text-amber-500'} />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      <p className="font-semibold text-stone-800 text-sm">{r.titre}</p>
                      <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                        r.priorite === 'urgent' ? 'bg-red-100 text-red-700' :
                        r.priorite === 'faible' ? 'bg-stone-100 text-stone-600' :
                        'bg-amber-100 text-amber-700'}`}>
                        {r.priorite}
                      </span>
                      {r.statut === 'terminé' && <span className="text-xs bg-emerald-100 text-emerald-700 px-2 py-0.5 rounded-full">Terminé</span>}
                    </div>
                    {r.description && <p className="text-sm text-stone-600 mt-1">{r.description}</p>}
                    <p className="text-xs text-stone-400 mt-1">
                      {isOverdue ? <span className="text-red-500 font-medium">En retard · </span> : ''}
                      {r.date_rappel}{r.heure_rappel ? ` à ${r.heure_rappel}` : ''}
                    </p>
                  </div>
                  <div className="flex gap-1">
                    {r.statut === 'actif' && (
                      <button onClick={() => handleDoneReminder(r.id)} title="Marquer terminé"
                        className="p-1.5 text-stone-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-md transition-colors">
                        <CheckCircle size={15} />
                      </button>
                    )}
                    <button onClick={() => handleDeleteReminder(r.id)} title="Supprimer"
                      className="p-1.5 text-stone-400 hover:text-red-600 hover:bg-red-50 rounded-md transition-colors">
                      <X size={15} />
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </TabsContent>

        {/* Résumé IA */}
        <TabsContent value="ia">
          <div className="bg-white border border-stone-200 rounded-xl p-5 space-y-4">
            <div className="flex items-center justify-between flex-wrap gap-3">
              <div>
                <h3 className="font-heading font-semibold text-stone-800">Résumé Médical IA</h3>
                <p className="text-xs text-stone-500 mt-0.5">Analyse intelligente du dossier patient</p>
              </div>
              <div className="flex items-center gap-3">
                <select
                  data-testid="ai-summary-model-selector"
                  value={aiModel}
                  onChange={e => setAiModel(e.target.value)}
                  className="px-3 py-1.5 border border-stone-300 rounded-lg text-sm bg-white focus:outline-none focus:border-emerald-500"
                >
                  {MODELS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
                <button
                  data-testid="generate-ai-summary-btn"
                  onClick={handleAiSummary}
                  disabled={aiLoading}
                  className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-60 text-white rounded-lg text-sm font-medium transition-colors"
                >
                  {aiLoading ? <Loader size={15} className="animate-spin" /> : <Sparkles size={15} />}
                  {aiLoading ? 'Génération...' : 'Générer le résumé'}
                </button>
              </div>
            </div>
            {aiSummary ? (
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-5">
                <div className="prose prose-sm prose-stone max-w-none">
                  <ReactMarkdown>{aiSummary}</ReactMarkdown>
                </div>
              </div>
            ) : (
              <div className="text-center py-12 text-stone-400">
                <Sparkles size={36} className="text-stone-200 mx-auto mb-3" />
                <p className="text-sm">Cliquez sur "Générer le résumé" pour analyser ce dossier</p>
                <p className="text-xs mt-1">{consultations.length} consultation(s) disponible(s)</p>
              </div>
            )}
          </div>
        </TabsContent>

        {/* Info */}
        <TabsContent value="info">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {[
              { title: 'Antécédents personnels', content: patient.antecedents_personnels },
              { title: 'Antécédents familiaux', content: patient.antecedents_familiaux },
              { title: 'Médicaments actuels', content: patient.medicaments_actuels },
            ].map(({ title, content }) => (
              <div key={title} className="bg-white border border-stone-200 rounded-xl p-5">
                <h3 className="font-heading font-semibold text-stone-700 text-xs uppercase tracking-wide mb-3">{title}</h3>
                <p className="text-sm text-stone-600 leading-relaxed">{content || 'Non renseigné'}</p>
              </div>
            ))}
            <div className="bg-white border border-stone-200 rounded-xl p-5">
              <h3 className="font-heading font-semibold text-stone-700 text-xs uppercase tracking-wide mb-3">Informations sociales</h3>
              <div className="space-y-2">
                <InfoItem label="Profession" value={patient.profession} />
                <InfoItem label="Situation familiale" value={patient.situation_familiale} />
                <InfoItem label="Adresse" value={patient.adresse} />
                <InfoItem label="Email" value={patient.email} />
              </div>
            </div>
          </div>
        </TabsContent>

        {/* RDV */}
        <TabsContent value="rdv">
          <div className="space-y-3">
            {appointments.length === 0 ? (
              <div className="bg-white border border-stone-200 rounded-xl p-10 text-center">
                <Calendar size={40} className="text-stone-200 mx-auto mb-3" />
                <p className="text-stone-400 text-sm">Aucun rendez-vous</p>
              </div>
            ) : appointments.map(a => (
              <div key={a.id} className="bg-white border border-stone-200 rounded-xl p-4 flex items-center gap-4">
                <div className="text-center w-14 flex-shrink-0">
                  <p className="font-bold text-emerald-600">{a.heure}</p>
                  <p className="text-xs text-stone-400">{a.date?.slice(5)?.replace('-','/')}</p>
                </div>
                <div className="flex-1">
                  <p className="text-sm font-medium text-stone-800">{a.motif}</p>
                  {a.notes && <p className="text-xs text-stone-500 mt-0.5">{a.notes}</p>}
                </div>
                <span className={`text-xs font-medium px-2.5 py-1 rounded-full ${
                  a.statut === 'confirmé' ? 'bg-emerald-100 text-emerald-700' :
                  a.statut === 'annulé' ? 'bg-red-100 text-red-700' :
                  'bg-blue-100 text-blue-700'}`}>
                  {a.statut}
                </span>
              </div>
            ))}
          </div>
        </TabsContent>
      </Tabs>

      {/* Reminder Modal */}
      {showReminderModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md" data-testid="reminder-modal">
            <div className="flex items-center justify-between px-6 py-4 border-b border-stone-200">
              <h2 className="font-heading font-semibold text-stone-800">Nouveau Rappel</h2>
              <button onClick={() => setShowReminderModal(false)} className="p-1 text-stone-400 hover:text-stone-600"><X size={20} /></button>
            </div>
            <div className="p-6 space-y-4">
              <div className="flex flex-col space-y-1">
                <label className="text-xs font-medium text-stone-600">Titre *</label>
                <input value={reminderForm.titre} onChange={e => setReminderForm(f => ({ ...f, titre: e.target.value }))}
                  placeholder="Ex: Contrôle HbA1c, Renouvellement ordonnance..."
                  className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500" />
              </div>
              <div className="flex flex-col space-y-1">
                <label className="text-xs font-medium text-stone-600">Description</label>
                <textarea value={reminderForm.description} onChange={e => setReminderForm(f => ({ ...f, description: e.target.value }))}
                  rows={2} placeholder="Détails du rappel..."
                  className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 resize-none" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="flex flex-col space-y-1">
                  <label className="text-xs font-medium text-stone-600">Date *</label>
                  <input type="date" value={reminderForm.date_rappel} onChange={e => setReminderForm(f => ({ ...f, date_rappel: e.target.value }))}
                    className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500" />
                </div>
                <div className="flex flex-col space-y-1">
                  <label className="text-xs font-medium text-stone-600">Heure</label>
                  <input type="time" value={reminderForm.heure_rappel} onChange={e => setReminderForm(f => ({ ...f, heure_rappel: e.target.value }))}
                    className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500" />
                </div>
              </div>
              <div className="flex flex-col space-y-1">
                <label className="text-xs font-medium text-stone-600">Priorité</label>
                <select value={reminderForm.priorite} onChange={e => setReminderForm(f => ({ ...f, priorite: e.target.value }))}
                  className="px-3 py-2 border border-stone-300 rounded-md text-sm bg-white focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500">
                  <option value="faible">Faible</option>
                  <option value="normal">Normal</option>
                  <option value="urgent">Urgent</option>
                </select>
              </div>
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t border-stone-200 bg-stone-50">
              <button onClick={() => setShowReminderModal(false)} className="px-5 py-2 border border-stone-300 bg-white text-stone-700 rounded-lg text-sm font-medium hover:bg-stone-100">Annuler</button>
              <button
                data-testid="save-reminder-button"
                onClick={handleSaveReminder}
                disabled={savingReminder || !reminderForm.titre || !reminderForm.date_rappel}
                className="px-5 py-2 bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-white rounded-lg text-sm font-medium"
              >
                {savingReminder ? 'Enregistrement...' : 'Enregistrer'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
